package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.path
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.Report
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ReportScreen(
    onBack: () -> Unit,
    onReportClick: (Report) -> Unit,
    onGenerateReport: () -> Unit
) {
    val userRole = LocalUserRole.current
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    
    var reports by remember { mutableStateOf<List<Report>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        repository.getReports().fold(
            onSuccess = {
                reports = it.sortedByDescending { r -> r.startDate }
                isLoading = false
            },
            onFailure = {
                error = it.message
                isLoading = false
            }
        )
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { ScreenTitle("Reports") },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    },
                    actions = {
                        if (userRole == UserRole.ADMIN) {
                            ToolbarPlusButton(onClick = onGenerateReport)
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (error != null) {
                    Text("Error: $error", modifier = Modifier.align(Alignment.Center), color = MaterialTheme.colorScheme.error)
                } else if (!isLoading && reports.isEmpty()) {
                    Text("No reports found.", modifier = Modifier.align(Alignment.Center))
                } else if (!isLoading) {
                    val groupedReports = remember(reports) {
                        reports.groupBy { report ->
                            try {
                                val date = kotlinx.datetime.LocalDate.parse(report.startDate)
                                val monthName = date.month.name.lowercase().replaceFirstChar { it.uppercase() }
                                "$monthName ${date.year}"
                            } catch (_: Exception) {
                                report.startDate.take(7)
                            }
                        }.entries.sortedByDescending { entry ->
                            entry.value.maxOfOrNull { it.startDate } ?: ""
                        }
                    }

                    LazyColumn(modifier = Modifier.fillMaxSize()) {
                        groupedReports.forEach { (monthYear, monthReports) ->
                            item(key = monthYear) {
                                Surface(
                                    color = MaterialTheme.colorScheme.surfaceVariant,
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Text(
                                        text = monthYear,
                                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                                        style = MaterialTheme.typography.titleSmall,
                                        fontWeight = FontWeight.Bold,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                }
                            }
                            items(monthReports, key = { it.id }) { report ->
                                val startDateFormatted = DateTimeUtils.formatIsoDisplayDate(report.startDate)
                                val endDateFormatted = DateTimeUtils.formatIsoDisplayDate(report.endDate)
                                ListItem(
                                    headlineContent = { 
                                        Text("$startDateFormatted to $endDateFormatted", fontWeight = FontWeight.Bold) 
                                    },
                                    supportingContent = if (userRole == UserRole.ADMIN) {
                                        { 
                                            Text("Total Spent: £${report.totalSpent}", color = MaterialTheme.colorScheme.secondary) 
                                        }
                                    } else null,
                                    modifier = Modifier.clickable { onReportClick(report) }
                                )
                                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                            }
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { /* Block touches */ },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }
}

@Composable
fun AddIcon() {
    val icon = remember {
        ImageVector.Builder(
            name = "Add",
            defaultWidth = 24.dp,
            defaultHeight = 24.dp,
            viewportWidth = 24f,
            viewportHeight = 24f
        ).path(
            fill = SolidColor(Color.Black),
            strokeLineWidth = 0f
        ) {
            moveTo(19f, 13f)
            horizontalLineToRelative(-6f)
            verticalLineToRelative(6f)
            horizontalLineToRelative(-2f)
            verticalLineToRelative(-6f)
            horizontalLineTo(5f)
            verticalLineToRelative(-2f)
            horizontalLineToRelative(6f)
            verticalLineTo(5f)
            horizontalLineToRelative(2f)
            verticalLineToRelative(6f)
            horizontalLineToRelative(6f)
            verticalLineToRelative(2f)
            close()
        }.build()
    }
    Icon(imageVector = icon, contentDescription = "Add", tint = LocalContentColor.current)
}
